import React, { useCallback, useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Switch,
  message,
  Breadcrumb,
  Spin,
  Row,
  Col,
  InputNumber,
  Radio,
} from 'antd';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
  CheckCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  getAssistants,
  getBot,
  createBot,
  updateBot,
} from '../../services/api';
import BotTriggerFields from '../../components/bots/BotTriggerFields';
import useCloudProviders from '../../hooks/useCloudProviders';
import useLlmModelPicker from '../../hooks/useLlmModelPicker';
import BotRateLimitField from '../../components/bots/BotRateLimitField';
import BotReplyDelayFields, { DEFAULT_REPLY_DELAY } from '../../components/bots/BotReplyDelayFields';
import BotAllowedChatIdsField from '../../components/bots/BotAllowedChatIdsField';
import BotTelegramAccountSection from '../../components/bots/BotTelegramAccountSection';
import PromptEditor from '../../components/prompt/PromptEditor';
import { bodyToBotLegacy, botLegacyToBody } from '../../components/prompt/promptUtils';
import {
  buildTriggerConfigPayload,
  normalizeTriggerConfigFromApi,
  normalizeAllowedChatIds,
  validateAllowedChatIds,
} from '../../utils/botDisplay';
import '../AddAssistant.css';
import './AddBot.css';

const PLATFORM_OPTIONS = [
  {
    key: 'telegram_userbot',
    title: 'Telegram Userbot',
    description: 'Listen in Telegram chats as a logged-in user account.',
    icon: <SendOutlined />,
    iconClass: 'create-collection-choice-card-icon-blue',
  },
];

function AddBot() {
  const { botId } = useParams();
  const isEditMode = Boolean(botId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { keycloak, initialized } = useKeycloak();
  const [form] = Form.useForm();

  const [selectedPlatform, setSelectedPlatform] = useState('telegram_userbot');
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoadDone, setEditLoadDone] = useState(!isEditMode);

  const [triggers, setTriggers] = useState(['direct']);
  const [questionDetection, setQuestionDetection] = useState('heuristic');
  const [skipRepliesToOthers, setSkipRepliesToOthers] = useState(true);
  const { cloudProviders } = useCloudProviders(keycloak);
  const questionLlmPicker = useLlmModelPicker({
    keycloak,
    cloudProviders,
    enabled: questionDetection === 'llm',
  });
  const [hasCredentials, setHasCredentials] = useState(false);

  const usageQuota = Form.useWatch('usage_quota', form);
  const assistantIdWatch = Form.useWatch('assistant_id', form);
  const selectedAssistant = assistants.find((a) => a.id === assistantIdWatch);

  const loadAssistants = useCallback(async () => {
    if (!keycloak?.token) return;
    try {
      const data = await getAssistants(keycloak, false);
      setAssistants(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.message || 'Failed to load assistants');
    }
  }, [keycloak]);

  const loadBot = useCallback(async () => {
    if (!isEditMode || !keycloak?.token) return;
    setLoading(true);
    try {
      const bot = await getBot(keycloak, botId);
      setSelectedPlatform(bot.platform);
      setHasCredentials(Boolean(bot.has_credentials));
      const tc = normalizeTriggerConfigFromApi(bot.trigger_config, bot.trigger_mode);
      setTriggers(tc.triggers);
      setQuestionDetection(tc.question_detection);
      setSkipRepliesToOthers(tc.skip_replies_to_others !== false);
      questionLlmPicker.hydrateFrom(tc.question_llm);
      form.setFieldsValue({
        name: bot.name,
        assistant_id: bot.assistant_id,
        enabled: bot.enabled,
        allowed_chat_ids: bot.settings?.allowed_chat_ids || [],
        rate_limit_per_minute: bot.settings?.rate_limit_per_minute,
        reply_delay: {
          ...DEFAULT_REPLY_DELAY,
          ...(bot.settings?.reply_delay || {}),
        },
        max_history_messages: bot.settings?.max_history_messages ?? 10,
        usage_quota: bot.usage_quota,
        usage_period: bot.usage_period,
        prompt_mode: bot.prompt_mode || 'assistant',
        custom_prompt: botLegacyToBody(bot.prompt_mode, bot.custom_prompt),
        prompt_placement: bot.prompt_placement || 'before_context',
        style_enabled: Boolean(bot.style_enabled),
        style_prompt: bot.style_prompt || '',
        relevance_check_enabled: Boolean(bot.relevance_check_enabled),
        relevance_scope: bot.relevance_scope || '',
        data_check_enabled: Boolean(bot.data_check_enabled),
        data_check_min_similarity: bot.data_check_min_similarity ?? 0.2,
        refusal_reply_enabled: Boolean(bot.refusal_reply_enabled),
        off_topic_message: bot.off_topic_message || '',
        no_data_message: bot.no_data_message || '',
      });
      setEditLoadDone(true);
    } catch (e) {
      message.error(e.message || 'Failed to load bot');
    } finally {
      setLoading(false);
    }
  }, [isEditMode, keycloak, botId, form]);

  useEffect(() => {
    if (!initialized) return;
    loadAssistants();
    loadBot();
  }, [initialized, loadAssistants, loadBot]);

  useEffect(() => {
    if (isEditMode) return;
    const raw = searchParams.get('assistant_id');
    if (!raw) return;
    const aid = parseInt(raw, 10);
    if (!Number.isFinite(aid)) return;
    form.setFieldsValue({ assistant_id: aid });
  }, [isEditMode, searchParams, form]);

  const buildPayload = (values) => {
    const questionLlm = questionLlmPicker.toBotQuestionLlm();
    const trigger_config = buildTriggerConfigPayload(
      triggers,
      questionDetection,
      questionLlm,
      skipRepliesToOthers,
    );
    const allowedChatIds = normalizeAllowedChatIds(values.allowed_chat_ids);
    const promptLegacy = bodyToBotLegacy(values.custom_prompt);
    return {
      name: values.name,
      assistant_id: values.assistant_id,
      platform: selectedPlatform,
      enabled: values.enabled !== false,
      trigger_config,
      trigger_mode: trigger_config.triggers[0],
      settings: {
        allowed_chat_ids: allowedChatIds,
        rate_limit_per_minute: values.rate_limit_per_minute || null,
        reply_delay: {
          enabled: Boolean(values.reply_delay?.enabled),
          base_seconds: values.reply_delay?.base_seconds ?? DEFAULT_REPLY_DELAY.base_seconds,
          per_char_ms: values.reply_delay?.per_char_ms ?? DEFAULT_REPLY_DELAY.per_char_ms,
          jitter: values.reply_delay?.jitter ?? DEFAULT_REPLY_DELAY.jitter,
          min_seconds: values.reply_delay?.min_seconds ?? DEFAULT_REPLY_DELAY.min_seconds,
          max_seconds: values.reply_delay?.max_seconds ?? DEFAULT_REPLY_DELAY.max_seconds,
        },
        max_history_messages: values.max_history_messages ?? 10,
      },
      usage_quota: values.usage_quota || null,
      usage_period: values.usage_quota ? (values.usage_period || null) : null,
      prompt_mode: promptLegacy.prompt_mode,
      custom_prompt: promptLegacy.custom_prompt,
      prompt_placement: values.prompt_placement || 'before_context',
      style_enabled: Boolean(values.style_enabled),
      style_prompt: values.style_prompt?.trim() || null,
      relevance_check_enabled: Boolean(values.relevance_check_enabled),
      relevance_scope: values.relevance_scope?.trim() || null,
      data_check_enabled: Boolean(values.data_check_enabled),
      data_check_min_similarity: values.data_check_min_similarity ?? 0.2,
      refusal_reply_enabled: Boolean(values.refusal_reply_enabled),
      off_topic_message: values.off_topic_message?.trim() || null,
      no_data_message: values.no_data_message?.trim() || null,
    };
  };

  const onFinish = async (values) => {
    if (!selectedPlatform) {
      message.error('Select a platform');
      return;
    }
    setLoading(true);
    try {
      const payload = buildPayload(values);
      if (isEditMode) {
        await updateBot(keycloak, botId, {
          name: payload.name,
          assistant_id: payload.assistant_id,
          enabled: payload.enabled,
          trigger_mode: payload.trigger_mode,
          trigger_config: payload.trigger_config,
          settings: payload.settings,
          usage_quota: payload.usage_quota,
          usage_period: payload.usage_period,
          prompt_mode: payload.prompt_mode,
          custom_prompt: payload.custom_prompt,
          prompt_placement: payload.prompt_placement,
          style_enabled: payload.style_enabled,
          style_prompt: payload.style_prompt,
          relevance_check_enabled: payload.relevance_check_enabled,
          relevance_scope: payload.relevance_scope,
          data_check_enabled: payload.data_check_enabled,
          data_check_min_similarity: payload.data_check_min_similarity,
          refusal_reply_enabled: payload.refusal_reply_enabled,
          off_topic_message: payload.off_topic_message,
          no_data_message: payload.no_data_message,
        });
        message.success('Bot updated');
        navigate('/channels/bots');
      } else {
        const created = await createBot(keycloak, payload);
        message.success('Bot created');
        if (created.platform === 'telegram_userbot') {
          navigate(`/channels/bots/${created.id}/connect`);
        } else {
          navigate('/channels/bots');
        }
      }
    } catch (e) {
      message.error(e.message || 'Failed to save bot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container add-assistant-page add-bot-page">
      <Breadcrumb
        className="add-assistant-breadcrumb"
        items={[
          { title: <Link to="/channels/bots">Bots</Link> },
          { title: isEditMode ? 'Edit Bot' : 'Create Bot' },
        ]}
      />

      <div className="collections-page-header add-assistant-page-header">
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">{isEditMode ? 'Edit Bot' : 'Create Bot'}</h2>
          <span className="page-header-subtitle">
            Configure how an assistant is delivered on a messaging channel
          </span>
        </div>
      </div>

      <div className="add-assistant-form-panel collections-table-wrapper">
        <Spin spinning={loading && isEditMode && !editLoadDone}>
          <Form
            form={form}
            className="add-assistant-form-inner"
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              enabled: true,
              allowed_chat_ids: [],
              reply_delay: { ...DEFAULT_REPLY_DELAY },
              prompt_mode: 'assistant',
              prompt_placement: 'before_context',
              style_enabled: false,
              max_history_messages: 10,
              relevance_check_enabled: false,
              data_check_enabled: false,
              data_check_min_similarity: 0.2,
              refusal_reply_enabled: false,
            }}
            scrollToFirstError
          >
            <div className="add-assistant-section">
              <div className="add-assistant-section-title">Platform</div>
              <p className="add-assistant-section-desc">Choose the messaging connector.</p>
            </div>

            <Form.Item label="Platform" required>
              <div className="create-collection-choice-grid create-collection-indexing-cards">
                {PLATFORM_OPTIONS.map(({ key, title, description, icon, iconClass }) => {
                  const selected = selectedPlatform === key;
                  return (
                    <button
                      type="button"
                      key={key}
                      disabled={isEditMode}
                      className={`create-collection-choice-card ${selected ? 'is-selected' : ''}`}
                      onClick={() => !isEditMode && setSelectedPlatform(key)}
                      aria-pressed={selected}
                    >
                      <div className={`create-collection-choice-card-icon ${iconClass}`}>
                        {React.cloneElement(icon, { style: { fontSize: 22 } })}
                      </div>
                      <div className="create-collection-choice-card-body">
                        <span className="create-collection-choice-card-title">{title}</span>
                        <span className="create-collection-choice-card-desc">{description}</span>
                      </div>
                      {selected && (
                        <CheckCircleOutlined className="create-collection-choice-card-check" aria-hidden />
                      )}
                    </button>
                  );
                })}
              </div>
            </Form.Item>

            <div className="add-assistant-section">
              <div className="add-assistant-section-title">Settings</div>
            </div>

            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input placeholder="Support Telegram bot" />
            </Form.Item>

            <Form.Item
              name="assistant_id"
              label="Assistant"
              rules={[{ required: true, message: 'Select an assistant' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Select assistant"
                options={assistants.map((a) => ({
                  value: a.id,
                  label: a.name || `Assistant #${a.id}`,
                }))}
              />
            </Form.Item>

            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>

            {isEditMode && selectedPlatform === 'telegram_userbot' && (
              <>
                <div className="add-assistant-section">
                  <div className="add-assistant-section-title">Telegram account</div>
                  <p className="add-assistant-section-desc">
                    Linked account used to send and receive messages.
                  </p>
                </div>
                <Form.Item label="Account">
                  <BotTelegramAccountSection
                    keycloak={keycloak}
                    botId={botId}
                    hasCredentials={hasCredentials}
                    onDisconnected={() => setHasCredentials(false)}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item label="When to respond">
              <BotTriggerFields
                triggers={triggers}
                onTriggersChange={setTriggers}
                questionDetection={questionDetection}
                onQuestionDetectionChange={setQuestionDetection}
                skipRepliesToOthers={skipRepliesToOthers}
                onSkipRepliesToOthersChange={setSkipRepliesToOthers}
                questionLlmPicker={questionLlmPicker}
                cloudProviders={cloudProviders}
                navigate={navigate}
              />
            </Form.Item>

            <Form.Item
              name="allowed_chat_ids"
              label="Allowed chat IDs"
              extra="Leave empty to allow all chats. Supergroups/channels: -100… or bare id (e.g. 5401748599). Legacy groups: -123…. Private chats: positive user id."
              normalize={normalizeAllowedChatIds}
              rules={[
                {
                  validator: (_, value) => {
                    const err = validateAllowedChatIds(value);
                    return err ? Promise.reject(new Error(err)) : Promise.resolve();
                  },
                },
              ]}
            >
              <BotAllowedChatIdsField botId={isEditMode ? botId : null} disabled={loading} />
            </Form.Item>

            <Form.Item name="rate_limit_per_minute" label="Rate limit">
              <BotRateLimitField />
            </Form.Item>

            <BotReplyDelayFields />

            <Row gutter={12}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="usage_quota" label="Usage quota">
                  <InputNumber
                    min={1}
                    style={{ width: '100%' }}
                    placeholder="Unlimited"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="usage_period"
                  label="Quota period"
                  dependencies={['usage_quota']}
                >
                  <Select
                    allowClear
                    placeholder="e.g. month"
                    disabled={!usageQuota}
                    options={[
                      { value: 'day', label: 'Per day' },
                      { value: 'week', label: 'Per week' },
                      { value: 'month', label: 'Per month' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="add-assistant-section">
              <div className="add-assistant-section-title">Prompt</div>
              <p className="add-assistant-section-desc">
                Channel-specific instructions. Start from the assistant&apos;s prompt, append your own, or write from scratch.
              </p>
            </div>

            <Form.Item name="custom_prompt" noStyle>
              <PromptEditor
                binding={
                  isEditMode && botId
                    ? {
                        targetType: 'bot',
                        targetId: Number(botId),
                        slot: 'custom_prompt',
                      }
                    : null
                }
                basePrompt={
                  selectedAssistant
                    ? {
                        label: selectedAssistant.name,
                        text: selectedAssistant.system_prompt || '',
                      }
                    : null
                }
                models={
                  selectedAssistant?.is_local === false
                    ? [selectedAssistant.cloud_model || selectedAssistant.model].filter(Boolean)
                    : [selectedAssistant?.model].filter(Boolean)
                }
                placeholder="Instructions specific to this delivery channel"
              />
            </Form.Item>

            <Form.Item
              name="max_history_messages"
              label="Chat history for answers"
              extra="How many recent user and assistant messages to send to the model (per Telegram chat). Older messages stay in logs but are not included in the prompt."
            >
              <InputNumber min={1} max={100} precision={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="prompt_placement"
              label="Prompt placement"
              extra="Where the assistant or bot prompt sits relative to retrieved knowledge in the system message."
            >
              <Radio.Group>
                <Radio value="before_context">Before knowledge context (default)</Radio>
                <Radio value="after_context">After knowledge context</Radio>
              </Radio.Group>
            </Form.Item>

            <div className="add-assistant-section">
              <div className="add-assistant-section-title">Response styling</div>
              <p className="add-assistant-section-desc">
                Optional second model pass to format replies after the grounded answer is written.
              </p>
            </div>

            <Form.Item
              name="style_enabled"
              label="Enable response styling"
              valuePropName="checked"
              extra="Runs a second model pass to format replies. Adds latency and extra token cost."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.style_enabled !== c.style_enabled}
            >
              {({ getFieldValue }) => getFieldValue('style_enabled') ? (
                <Form.Item name="style_prompt" noStyle>
                  <PromptEditor
                    binding={
                      isEditMode && botId
                        ? { targetType: 'bot', targetId: Number(botId), slot: 'style_prompt' }
                        : null
                    }
                    features={{ library: false }}
                    placeholder="e.g. Short bullet points, friendly tone, end with a call to action"
                  />
                </Form.Item>
              ) : null}
            </Form.Item>

            <div className="add-assistant-section">
              <div className="add-assistant-section-title">Answer gates</div>
              <p className="add-assistant-section-desc">
                Optional checks before the bot replies. Declined questions are logged for review.
              </p>
            </div>

            <Form.Item name="relevance_check_enabled" label="Relevance check" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.relevance_check_enabled !== c.relevance_check_enabled}
            >
              {({ getFieldValue }) => getFieldValue('relevance_check_enabled') ? (
                <Form.Item name="relevance_scope" noStyle>
                  <PromptEditor
                    binding={
                      isEditMode && botId
                        ? { targetType: 'bot', targetId: Number(botId), slot: 'relevance_scope' }
                        : null
                    }
                    features={{ library: false, assist: true, publish: false }}
                    placeholder="Topics this bot should answer about"
                  />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item name="data_check_enabled" label="Data availability check" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.data_check_enabled !== c.data_check_enabled}
            >
              {({ getFieldValue }) => getFieldValue('data_check_enabled') ? (
                <Form.Item
                  name="data_check_min_similarity"
                  label="Minimum retrieval similarity"
                  extra="Vector collections only. Decline when top chunk score is below this threshold."
                >
                  <InputNumber min={0} max={1} step={0.05} style={{ width: 160 }} />
                </Form.Item>
              ) : null}
            </Form.Item>

            <Form.Item
              name="refusal_reply_enabled"
              label="Reply when declining"
              valuePropName="checked"
              extra="Off by default the bot stays silent. Turn on to send a short message when it declines a question."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(p, c) => p.refusal_reply_enabled !== c.refusal_reply_enabled}
            >
              {({ getFieldValue }) => getFieldValue('refusal_reply_enabled') ? (
                <>
                  <Form.Item
                    name="off_topic_message"
                    label="Off-topic reply"
                    extra="Sent when a message is outside the domain scope. Leave empty to stay silent for off-topic."
                  >
                    <Input.TextArea rows={2} placeholder="e.g. Sorry, I only answer questions about ..." />
                  </Form.Item>
                  <Form.Item
                    name="no_data_message"
                    label="No-data reply"
                    extra="Sent when the knowledge base has no relevant answer. Leave empty to stay silent."
                  >
                    <Input.TextArea rows={2} placeholder="e.g. I don't have information on that yet." />
                  </Form.Item>
                </>
              ) : null}
            </Form.Item>

            <div className="add-assistant-form-footer">
              <div className="add-assistant-form-footer-actions">
                <Button type="primary" htmlType="submit" loading={loading}>
                  {isEditMode ? 'Save changes' : 'Continue'}
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={() => navigate('/channels/bots')}>
                  Cancel
                </Button>
              </div>
            </div>
          </Form>
        </Spin>
      </div>
    </div>
  );
}

export default AddBot;
